'use client';

import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from '@weareconceptstudio/core';
import useSWR from 'swr';

const LazyLoadWithData = ({
	fetchKey,
	fetcher,
	children,
	fallback,
	threshold = 0.1,
	delay = 0,
	swrOptions = {},
}) => {
	const { translate } = useTranslation();
	const { ref, inView } = useInView({ triggerOnce: true, threshold, delay });
	const resolvedFallback = fallback ?? <div>{translate('common.loadingContent')}</div>;

	// Only trigger SWR when inView
	const shouldFetch = useMemo(() => inView && fetchKey != null, [inView, fetchKey]);

	const { data, error, isLoading } = useSWR(shouldFetch ? fetchKey : null, fetcher, {
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
		dedupingInterval: 1000 * 60 * 10,
		...swrOptions,
	});

	// Show fallback until inView + data loaded
	// Also wait while data is still undefined (e.g. the render right after inView
	// flips, or an empty API response) so children never receive undefined.
	if (!inView || isLoading || (data === undefined && !error)) return <div ref={ref}>{resolvedFallback}</div>;

	if (error) return <div ref={ref}>{translate('common.errorLoadingContent')}</div>;

	// Render children with data
	return <div ref={ref}>{children(data)}</div>;
};

export default LazyLoadWithData;
